import React, {useEffect, useState} from 'react';
import {
    ActionButton,
    ColumnOne,
    ColumnTwo,
    DetailedFiltersContent,
    DropdownWrapper,
    FilterCategories
} from './FileTypePickerAdvanced.style';
import {t} from 'i18next';
import {mainFileTypeResource} from './resources/MainFileTypeResource';
import {config} from '../../init/config';
import {getFontSize, isNullOrEmpty} from '../../utils';
import {FileTypePicker} from './FileTypePicker';
import {FileType, getFileTypeResource} from './FileTypeResourceRegister';
import {fileTypeMainToSubResource} from './resources/FileTypeMainToSubResource';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {
    DROPDOWN_OPTION,
    getEmptyDropdownOptionProps,
    getFilteredDropdownOptionProps,
    getLocalizedDropdownOptionProps,
    SELECT_TYPE
} from '../../utils/DropdownOptionPropsUtils';
import {DropdownOptionProps} from '../../models/DropdownProps';

export interface FileTypePickerAdvancedProps {
    readonly isMobileResolution: boolean;
    readonly resetState: boolean;
    readonly resetInput: boolean;
    readonly fileTypes: string[];
    onChange: (fileTypeSelection: string[]) => void;
}

const getMainFileTypeToSubTypeMap = (): Map<string, string[]> => {
    let mappedSubFilterTypes: string[] = [];
    let mainFileTypeToSubTypeScope = new Map<string, string[]>();
    fileTypeMainToSubResource
        .filter(resourceTypeMap => ((isNullOrEmpty(resourceTypeMap.subType)) || (config.SUB_FILTERS.includes(resourceTypeMap.subType))))
        .map(resourceTypeMap => {
            if (!mappedSubFilterTypes.includes(resourceTypeMap.subType)) {
                mappedSubFilterTypes.push(resourceTypeMap.subType);
            }

            let subTypesTypes: string[] = mainFileTypeToSubTypeScope.get(resourceTypeMap.mainType) ?? [];
            if (subTypesTypes && (subTypesTypes.length > 0)) {
                mainFileTypeToSubTypeScope.set(resourceTypeMap.mainType, Array.from(new Set([...subTypesTypes, resourceTypeMap.subType])));
            } else {
                mainFileTypeToSubTypeScope.set(resourceTypeMap.mainType, [SELECT_TYPE, resourceTypeMap.subType]);
            }
        })

    mainFileTypeResource
        .filter(type => (!Array.from(mainFileTypeToSubTypeScope.keys()).includes(type.name)))
        .filter(type => ((isNullOrEmpty(type.value)) || (config.MAIN_FILTERS.includes(type.value))))
        .map(type => {
            mainFileTypeToSubTypeScope.set(type.name,
                Array.from(new Set(fileTypeMainToSubResource
                    .filter(resourceTypeMap => (type.name === resourceTypeMap.mainType))
                    .map(resourceTypeMap => resourceTypeMap.subType))));
        });

    let otherSubTypes = config.SUB_FILTERS.filter(type => (!mappedSubFilterTypes.includes(type)));
    if (otherSubTypes && (otherSubTypes.length > 0)) {
        let mappedOtherSubTypes = mainFileTypeToSubTypeScope.get(FileType.OTHER);
        if (mappedOtherSubTypes && (mappedOtherSubTypes.length > 0)) {
            otherSubTypes = [...mappedOtherSubTypes, ...otherSubTypes];
        }

        mainFileTypeToSubTypeScope.set(FileType.OTHER, Array.from(new Set(otherSubTypes)));
    }

    return mainFileTypeToSubTypeScope;
}

const getSubFileTypeOptions = (mainFileTypeSelectionState: string, mainFileTypeToSubTypeScope: Map<string, string[]>): DropdownOptionProps[] => {
    let options: DropdownOptionProps[] = [];
    options.push(getEmptyDropdownOptionProps(SELECT_TYPE, SELECT_TYPE, DROPDOWN_OPTION));
    if (isNullOrEmpty(mainFileTypeSelectionState)) {
        return options;
    }

    let resourceType = getFileTypeResource(mainFileTypeSelectionState);
    if (!resourceType) {
        return options;
    }

    let subTypes = mainFileTypeToSubTypeScope.get(mainFileTypeSelectionState) ?? [];
    if ((!subTypes) || (subTypes.length === 0)) {
        return options;
    }

    resourceType
        .filter(type => ((subTypes.includes(type.name))
            && (!type.name.includes(SELECT_TYPE))))
        .map(type => options.push({
            // name: getLocalizedFileTypeNameToOptionPropName(type.name),
            name: type.name,
            value: type.value
        }));

    if (FileType.OTHER === mainFileTypeSelectionState) {
        subTypes.map(type => {
            options.push({
                // name: getLocalizedFileTypeNameToOptionPropName(type.type),
                name: type,
                value: type
            })
        });
    }

    return Array.from(new Set(options));
};

/**
 * @name FileTypePickerAdvanced
 * @description
 * This component is responsible for rendering the detailed filter picker in the application.
 * It allows users to select main and sub filters for file types. The available filters are dynamically
 * loaded from a configuration. The selected filters are then communicated back to the parent component
 * through a callback function. This component maintains its own state for the currently selected main
 * and sub filters.
 */
export const FileTypePickerAdvanced: React.VFC<FileTypePickerAdvancedProps> = (props) => {
    const [mainFileTypeToSubTypeScope] = useState<Map<string, string[]>>(getMainFileTypeToSubTypeMap());
    const [fileSubTypeSelection, setFileSubTypeSelection] = useState<string[]>(props.fileTypes ?? []);
    const [mainFileTypeInput, setMainFileTypeInput] = useState<string>('');
    const [subFileTypeOptions, setSubFileTypeOptions] = useState<DropdownOptionProps[]>(getSubFileTypeOptions(mainFileTypeInput, mainFileTypeToSubTypeScope));
    const [mainFileTypeOptions] = useState<DropdownOptionProps[]>(getFilteredDropdownOptionProps(mainFileTypeResource, Array.from(mainFileTypeToSubTypeScope.keys()), SELECT_TYPE, DROPDOWN_OPTION));
    const [mainFileTypeOptionsUsed, setMainFileTypeOptionsUsed] = useState<string[]>([]);
    const [isResetMainFileType, setIsResetMainFileType] = useState<boolean>(false);
    const [isResetSubFileType, setIsResetSubFileType] = useState<boolean>(false);
    const blankLocalizedDropdownOptionProps = getLocalizedDropdownOptionProps(SELECT_TYPE, SELECT_TYPE, DROPDOWN_OPTION);

    const getFilteredMainFileTypeOptions = (): DropdownOptionProps[] => {
        if ((!mainFileTypeOptions) || (mainFileTypeOptions.length === 0)) {
            return [];
        }

        let options: DropdownOptionProps[] = [];
        options.push(getEmptyDropdownOptionProps(SELECT_TYPE, SELECT_TYPE, DROPDOWN_OPTION));
        mainFileTypeOptions
            .filter(type => (!mainFileTypeOptionsUsed.includes('' + (type.value ?? ''))))
            .map(type => options.push(type));

        return Array.from(new Set(options));
    }

    const getFilteredSubTypeOptions = (selectionOptions: DropdownOptionProps[]): DropdownOptionProps[] => {
        let options: DropdownOptionProps[] = [];
        if ((!selectionOptions) || (selectionOptions.length === 0)) {
            options.push(getEmptyDropdownOptionProps(SELECT_TYPE, SELECT_TYPE, DROPDOWN_OPTION));
            return options;
        }
        selectionOptions.map(type => options.push(type));

        return Array.from(new Set(options));
    }

    const setState = (selectionState: string[], selectionOptions: DropdownOptionProps[], resetInput: boolean) => {
        selectionState = Array.from(new Set(selectionState.filter(type => (!isNullOrEmpty(type)))));
        setFileSubTypeSelection(selectionState);
        setSubFileTypeOptions(getFilteredSubTypeOptions(selectionOptions));
        if ((selectionOptions.length === 1) && (selectionOptions[0].name === blankLocalizedDropdownOptionProps)) {
            setMainFileTypeInput('');
        }

        if ((!isNullOrEmpty(mainFileTypeInput)) || (isResetMainFileType && isResetSubFileType)) {
            props.onChange(selectionState);
        }

        if (resetInput) {
            if ((!isNullOrEmpty(mainFileTypeInput)) && (fileSubTypeSelection.length > 0)) {
                setMainFileTypeInput('');
                setIsResetMainFileType(true);
            }

            setMainFileTypeOptionsUsed([]);
            setIsResetSubFileType(true);
        }
    };

    useEffect(() => {
        let activeMainFileTypes: string[] = [];
        mainFileTypeToSubTypeScope.forEach((values, keys) => {
            let selectedTypes = [...props.fileTypes, SELECT_TYPE];
            let result = values.filter(value => (!selectedTypes.includes(value)));
            if (result.length > 0) {
                activeMainFileTypes.push(keys);
            }
        })

        if (mainFileTypeOptionsUsed.includes(mainFileTypeInput)) {
            setMainFileTypeInput('');
            setIsResetMainFileType(false);
            setIsResetSubFileType(true);
        }

        setMainFileTypeOptionsUsed(mainFileTypeOptionsUsed.filter(type => (!activeMainFileTypes.includes(type))));
        let selectionOptions = getSubFileTypeOptions(mainFileTypeInput, mainFileTypeToSubTypeScope)
            .filter(type => (props.fileTypes && (!props.fileTypes.includes(type.name))));
        setSubFileTypeOptions(selectionOptions);
        setFileSubTypeSelection(props.fileTypes ?? []);
    }, [props.fileTypes]);

    useEffect(() => {
        if (props.resetState) {
            setState([], getSubFileTypeOptions('', mainFileTypeToSubTypeScope), true);
        }
    }, [props.resetState]);

    const handleMainFileTypeFilterInput = (fileTypeSelection: string) => {
        setMainFileTypeInput(fileTypeSelection);
        let subTypeOptions = [];
        if (fileTypeSelection !== blankLocalizedDropdownOptionProps) {
            subTypeOptions = getSubFileTypeOptions(fileTypeSelection, mainFileTypeToSubTypeScope)
                .filter(type => (!fileSubTypeSelection.includes(type.name)));
            setSubFileTypeOptions(subTypeOptions);
        } else {
            subTypeOptions = getFilteredSubTypeOptions([]);
            setSubFileTypeOptions(subTypeOptions);
        }

        setIsResetMainFileType(false);
        setState(fileSubTypeSelection, subTypeOptions, false);
    };

    const handleMainFileTypeFilterAdd = () => {
        let selectionState = Array.from(new Set(fileSubTypeSelection.concat(subFileTypeOptions
            .filter(type => ((!isNullOrEmpty(type.name)) && (blankLocalizedDropdownOptionProps !== type.name)))
            .map(type => type.name))));
        setMainFileTypeOptionsUsed([...mainFileTypeOptionsUsed, mainFileTypeInput]);
        setMainFileTypeInput('');
        setIsResetMainFileType(true);
        setIsResetSubFileType(true);
        setState(selectionState, [], props.resetInput);
    }

    const handleSubFileTypeFilterInput = (fileTypeSelection: string) => {
        handleSubFileTypeFilterAdd(fileTypeSelection);
    };

    const handleSubFileTypeFilterAdd = (fileTypeSelection: string) => {
        let selectionState = Array.from(new Set([...fileSubTypeSelection, fileTypeSelection].filter(type => (!isNullOrEmpty(type)))));
        let filteredSubLists = subFileTypeOptions.filter(type => (!selectionState.includes(type.name)));
        if ((filteredSubLists.length === 1) && (filteredSubLists[0].name === blankLocalizedDropdownOptionProps)) {
            setMainFileTypeOptionsUsed([...mainFileTypeOptionsUsed, mainFileTypeInput]);
            setMainFileTypeInput('');
            setIsResetMainFileType(true);
        }

        setIsResetSubFileType(true);
        setState(selectionState, filteredSubLists, props.resetInput);
    }

    const handleFileTypeFilterReset = () => {
        setState([], getSubFileTypeOptions('', mainFileTypeToSubTypeScope), true);
        setMainFileTypeOptionsUsed([]);
        setMainFileTypeInput('');
        setIsResetMainFileType(true);
        setIsResetSubFileType(true);
    }

    const isResetMainFileTypePicker = (): boolean => {
        return ((isNullOrEmpty(mainFileTypeInput) || (SELECT_TYPE === mainFileTypeInput))) as boolean;
    }

    const isSybTypeFileTypePickerActive = (): boolean => {
        return ((subFileTypeOptions === undefined) || (!subFileTypeOptions) || (subFileTypeOptions.length === 0)
            || ((subFileTypeOptions.length === 1) && (subFileTypeOptions[0].name === blankLocalizedDropdownOptionProps))) as boolean;
    };

    let subTypeFilePickerDisabled = (isSybTypeFileTypePickerActive());
    let resetMainFileTypePicker: boolean = (isResetMainFileType && isResetMainFileTypePicker()) as boolean;
    let resetSubTypeFilePicker: boolean = (isResetSubFileType || resetMainFileTypePicker) as boolean;

    const isVisibleResetButton = (): boolean => {
        return (fileSubTypeSelection && (fileSubTypeSelection.length > 0)) as boolean;
    }

    let visibleResetButton = isVisibleResetButton();
    let fontSize = getFontSize([
        t(DROPDOWN_OPTION + 'COMPONENT_ADD_FILTER_LABEL'),
        t(DROPDOWN_OPTION + 'COMPONENT_RESET_FILTER_LABEL')
    ], 2);

    return (
        <DetailedFiltersContent id={generateIdentifier()} isMobileResolution={props.isMobileResolution}>
            <ColumnOne id={generateIdentifier()}>
                <FilterCategories id={generateIdentifier()}>
                    {t('FILTERS_AREA_FILE_TYPE', {count: props.fileTypes.length})}
                </FilterCategories>
            </ColumnOne>
            <ColumnTwo id={generateIdentifier()} isMobileResolution={props.isMobileResolution}>
                <DropdownWrapper id={generateIdentifier()}>
                    <FileTypePicker dataSource={getFilteredMainFileTypeOptions()}
                                    defaultValue={''}
                                    resetState={resetMainFileTypePicker}
                                    resetInput={resetMainFileTypePicker}
                                    disabled={false}
                                    onChange={handleMainFileTypeFilterInput}/>
                    <ActionButton id={generateIdentifier()}
                                  key='filterMainFileTypesAddButton'
                                  icon='add'
                                  mode='primary'
                                  disabled={isNullOrEmpty(mainFileTypeInput)}
                                  type='button'
                                  label={t(DROPDOWN_OPTION + 'COMPONENT_ADD_FILTER_LABEL')}
                                  title={t(DROPDOWN_OPTION + 'COMPONENT_ADD_FILTER_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleMainFileTypeFilterAdd}/>
                </DropdownWrapper>
                <DropdownWrapper id={generateIdentifier()}>
                    <FileTypePicker dataSource={subFileTypeOptions}
                                    defaultValue={''}
                                    resetState={resetSubTypeFilePicker}
                                    resetInput={resetSubTypeFilePicker}
                                    disabled={subTypeFilePickerDisabled}
                                    onChange={handleSubFileTypeFilterInput}/>
                    <ActionButton id={generateIdentifier()}
                                  key='filterFileTypesResetButton'
                                  icon='reset'
                                  mode='secondary'
                                  disabled={!visibleResetButton}
                                  type='button'
                                  label={t(DROPDOWN_OPTION + 'COMPONENT_RESET_FILTER_LABEL')}
                                  title={t(DROPDOWN_OPTION + 'COMPONENT_RESET_FILTER_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleFileTypeFilterReset}/>
                </DropdownWrapper>
            </ColumnTwo>
        </DetailedFiltersContent>
    );
};